/* Accessible collection tabs. Product ordering remains with Shopify and its apps. */
(() => {
  'use strict';
  const initialized = new WeakSet();
  const initializedArrivals = new WeakSet();
  function initTabs(root) {
    const groups = [];
    if (root.matches && root.matches('[data-fabric-tabs]')) groups.push(root);
    if (root.querySelectorAll) groups.push(...root.querySelectorAll('[data-fabric-tabs]'));
    groups.forEach(group => {
      if (initialized.has(group)) return;
      const tabs = [...group.querySelectorAll('[data-fabric-tab]')];
      const panels = [...group.querySelectorAll('[role="tabpanel"]')];
      const collectionLink = group.querySelector('[data-fabric-collection-link]');
      if (!tabs.length) return;
      initialized.add(group);
      function activate(tab) {
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
        if (collectionLink && tab.dataset.collectionUrl) {
          collectionLink.href = tab.dataset.collectionUrl;
          collectionLink.textContent = tab.dataset.collectionLabel;
        }
      }
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') target = 0;
          else if (event.key === 'End') target = tabs.length - 1;
          else return;
          event.preventDefault();
          activate(tabs[target]);
          tabs[target].focus();
        });
      });
    });
  }
  function initArrivals(root) {
    const groups = [];
    if (root.matches && root.matches('[data-fabric-arrivals]')) groups.push(root);
    if (root.querySelectorAll) groups.push(...root.querySelectorAll('[data-fabric-arrivals]'));
    groups.forEach(section => {
      if (initializedArrivals.has(section)) return;
      const viewport = section.querySelector('[data-fabric-arrivals-viewport]');
      const original = section.querySelector('[data-fabric-arrivals-group]');
      const pause = section.querySelector('[data-fabric-arrivals-pause]');
      if (!viewport || !original || original.querySelectorAll('.grid-product').length < 2) {
        if (pause) pause.hidden = true;
        return;
      }
      initializedArrivals.add(section);
      const motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : {matches:false};
      let userPaused = false, hover = false, focus = false, touching = false;
      let visible = !('IntersectionObserver' in window), frame = 0, previousTime = 0, distance = 0;
      function rebuild() {
        section.querySelectorAll('[data-fabric-arrivals-copy]').forEach(copy => copy.remove());
        distance = original.getBoundingClientRect().width + (parseFloat(getComputedStyle(original.parentElement).gap) || 0);
        if (!distance) return;
        const copies = Math.ceil(viewport.clientWidth / distance);
        for (let i = 0; i < copies; i++) {
          const copy = original.cloneNode(true);
          copy.removeAttribute('data-fabric-arrivals-group');
          copy.setAttribute('data-fabric-arrivals-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
          copy.querySelectorAll('a,button,input,[tabindex]').forEach(el => el.tabIndex = -1);
          copy.querySelectorAll('.quick-product__btn').forEach(el => el.remove());
          original.parentElement.append(copy);
        }
        viewport.scrollLeft %= distance;
      }
      function step(time) {
        if (previousTime) viewport.scrollLeft += Math.min(time - previousTime, 64) * 0.025;
        if (distance && viewport.scrollLeft >= distance) viewport.scrollLeft %= distance;
        previousTime = time;
        frame = requestAnimationFrame(step);
      }
      function update() {
        pause.hidden = motion.matches;
        pause.textContent = userPaused ? 'Play' : 'Pause';
        pause.setAttribute('aria-label', userPaused ? 'Resume scrolling' : 'Pause scrolling');
        pause.setAttribute('aria-pressed', String(userPaused));
        const running = visible && !motion.matches && !userPaused && !hover && !focus && !touching && !document.hidden;
        if (running && !frame) frame = requestAnimationFrame(step);
        if (!running && frame) { cancelAnimationFrame(frame); frame = 0; previousTime = 0; }
      }
      pause.addEventListener('click', () => { userPaused = !userPaused; update(); });
      section.addEventListener('mouseenter', () => { hover = true; update(); });
      section.addEventListener('mouseleave', () => { hover = false; update(); });
      section.addEventListener('focusin', () => { focus = true; update(); });
      section.addEventListener('focusout', event => { focus = section.contains(event.relatedTarget); update(); });
      viewport.addEventListener('pointerdown', () => { touching = true; update(); });
      function release() { touching = false; update(); }
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
      function resize() { rebuild(); update(); }
      window.addEventListener('resize', resize);
      document.addEventListener('visibilitychange', update);
      if (motion.addEventListener) motion.addEventListener('change', update);
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); });
        observer.observe(viewport);
      }
      function unload(event) {
        if (!event.target.contains(section)) return;
        cancelAnimationFrame(frame);
        if (observer) observer.disconnect();
        window.removeEventListener('resize', resize);
        window.removeEventListener('pointerup', release);
        window.removeEventListener('pointercancel', release);
        document.removeEventListener('visibilitychange', update);
        document.removeEventListener('shopify:section:unload', unload);
        if (motion.removeEventListener) motion.removeEventListener('change', update);
        initializedArrivals.delete(section);
      }
      document.addEventListener('shopify:section:unload', unload);
      rebuild();
      update();
    });
  }
  initTabs(document);
  initArrivals(document);
  document.addEventListener('shopify:section:load', event => { initTabs(event.target); initArrivals(event.target); });
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
    if (node.nodeType === 1) { initTabs(node); initArrivals(node); }
  }))).observe(document.body, {childList: true, subtree: true});
})();
